import React, { useState } from 'react';
import { CheckCircle2, Loader2, Send, MessageCircle, ShieldCheck } from 'lucide-react';
import { submitContactForm } from '../lib/api';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';
import { TREATMENTS } from '../data/treatments';
import { track } from '../lib/analytics';

const COUNTRIES = [
  'Vietnam', 'United States', 'United Kingdom', 'Australia', 'Canada',
  'Singapore', 'Malaysia', 'Thailand', 'Philippines', 'Indonesia',
  'Japan', 'South Korea', 'France', 'Germany', 'Other',
];

const CITIES = ['Ho Chi Minh City', 'Hanoi', 'Da Nang', 'Nha Trang', 'Hai Phong', 'No Preference'];

// Enquiry-based locations — coordination support only; not every treatment is
// available in each of these towns.
const ENQUIRY_CITIES = ['Huế (Hue)', 'Lăng Cô (Lang Co)', 'Đồng Hới (Dong Hoi)', 'Phong Nha', 'Sapa (Sa Pa)'];

const BUDGETS = [
  'Under $500',
  '$500 – $1,500',
  '$1,500 – $5,000',
  '$5,000 – $15,000',
  '$15,000+',
  'Not sure yet',
];

/**
 * Reusable appointment-request form used on the homepage and treatment
 * detail pages. Posts to the existing /contact endpoint and offers a
 * WhatsApp deep-link follow-up when contact info is available.
 *
 * Props:
 *  - defaultTreatment: pre-fills the Treatment Needed field
 *  - variant: 'card' (default, on a white card) or 'plain' (no card chrome)
 *  - title: optional override for the section heading
 */
const RequestAppointmentForm = ({
  defaultTreatment = '',
  defaultNotes = '',
  variant = 'card',
  title = 'Request Treatment',
  subtitle = 'Tell us what you need — our team reviews every request personally and matches you with a selected trusted clinic partner.',
  testIdPrefix = 'req',
}) => {
  const { contactInfo } = useContactInfo();
  const [form, setForm] = useState({
    fullName: '',
    whatsapp: '',
    email: '',
    country: '',
    treatment: defaultTreatment,
    city: '',
    date: '',
    budget: '',
    notes: defaultNotes,
    photo: null,
    consent: false,
  });
  const [status, setStatus] = useState({ loading: false, sent: false, error: '' });

  const onChange = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const onCheck = (k) => (e) =>
    setForm((prev) => ({ ...prev, [k]: e.target.checked }));
  const onFile = (e) => {
    const f = e.target.files?.[0];
    setForm((prev) => ({ ...prev, photo: f || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName || !form.whatsapp || !form.treatment) {
      setStatus({ loading: false, sent: false, error: 'Please complete your name, WhatsApp number and treatment.' });
      return;
    }
    if (!form.consent) {
      setStatus({ loading: false, sent: false, error: 'Please acknowledge the platform disclaimer to continue.' });
      return;
    }
    setStatus({ loading: true, sent: false, error: '' });

    const message = [
      `New treatment request from ${form.fullName}.`,
      `Treatment: ${form.treatment}`,
      `WhatsApp: ${form.whatsapp}`,
      form.email && `Email: ${form.email}`,
      form.country && `Country: ${form.country}`,
      form.city && `Preferred city: ${form.city}`,
      form.date && `Preferred date: ${form.date}`,
      form.budget && `Budget: ${form.budget}`,
      form.photo && `Photo attached: ${form.photo.name} (${Math.round(form.photo.size / 1024)} KB)`,
      form.notes && `Notes: ${form.notes}`,
    ].filter(Boolean).join('\n');

    try {
      await submitContactForm({
        name: form.fullName,
        email: form.email || `wa-${form.whatsapp.replace(/[^0-9]/g, '')}@vietclinics-request.com`,
        phone: form.whatsapp,
        subject: `Treatment Request — ${form.treatment}`,
        message,
      });
      track('appointment_request_submit', {
        treatment: form.treatment,
        country: form.country || 'unknown',
        city: form.city || 'unknown',
        budget: form.budget || 'unknown',
        status: 'delivered',
      });
    } catch (err) {
      // Even if the backend route is unavailable, we still show success
      // and let the user reach out via WhatsApp directly. This is
      // intentional — the form must never block the conversion path.
      // We log so failed submissions remain observable in production.
      // eslint-disable-next-line no-console
      console.warn('[RequestAppointmentForm] submission failed, fallback to WhatsApp', err);
      track('appointment_request_submit', {
        treatment: form.treatment,
        country: form.country || 'unknown',
        city: form.city || 'unknown',
        budget: form.budget || 'unknown',
        status: 'fallback',
        error: err?.message || 'unknown',
      });
    }
    setStatus({ loading: false, sent: true, error: '' });
  };

  const whatsappHref = getWhatsAppHref(
    contactInfo,
    `Hi VietClinics! I just submitted an appointment request for ${form.treatment || 'a treatment'}. My name is ${form.fullName || ''}.`
  );

  const wrap = variant === 'card'
    ? 'bg-white rounded-[28px] border border-slate-100 p-6 sm:p-10'
    : '';
  const shadow = variant === 'card'
    ? { boxShadow: '0 20px 60px rgba(15,42,85,0.08)' }
    : {};

  if (status.sent) {
    return (
      <div className={wrap} style={shadow} data-testid={`${testIdPrefix}-form-success`}>
        <div className="text-center max-w-md mx-auto py-6">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 flex items-center justify-center mb-4">
            <CheckCircle2 className="h-7 w-7 text-emerald-500" />
          </div>
          <h3 className="text-2xl font-extrabold text-slate-900 tracking-tight">
            Request received
          </h3>
          <p className="text-slate-500 mt-2 leading-relaxed">
            Thanks {form.fullName.split(' ')[0]}. Our team or a selected clinic partner will contact you shortly with the next steps — pricing guidance, availability and appointment options.
          </p>
          {whatsappHref && (
            <a
              href={whatsappHref}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe57] text-white font-bold rounded-full px-6 py-3 transition-colors"
              data-testid={`${testIdPrefix}-form-whatsapp-link`}
            >
              <MessageCircle className="h-4 w-4" />
              Continue on WhatsApp
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={wrap} style={shadow}>
      <div className="mb-6">
        <p className="text-[11px] font-bold text-[#1FA7A1] uppercase tracking-widest mb-2">
          Treatment Request
        </p>
        <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
          {title}
        </h3>
        <p className="text-slate-500 mt-2 text-sm sm:text-base leading-relaxed">{subtitle}</p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid={`${testIdPrefix}-form`}>
        <Field label="Full Name *">
          <input
            type="text"
            required
            value={form.fullName}
            onChange={onChange('fullName')}
            placeholder="Jane Nguyen"
            className={inputCls}
            data-testid={`${testIdPrefix}-input-name`}
          />
        </Field>
        <Field label="WhatsApp Number *">
          <input
            type="tel"
            required
            value={form.whatsapp}
            onChange={onChange('whatsapp')}
            placeholder="+84 ..."
            className={inputCls}
            data-testid={`${testIdPrefix}-input-whatsapp`}
          />
        </Field>

        <Field label="Email">
          <input
            type="email"
            value={form.email}
            onChange={onChange('email')}
            placeholder="you@example.com"
            className={inputCls}
            data-testid={`${testIdPrefix}-input-email`}
          />
        </Field>

        <Field label="Country">
          <select value={form.country} onChange={onChange('country')} className={inputCls} data-testid={`${testIdPrefix}-input-country`}>
            <option value="">Select your country</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>

        <Field label="Treatment Interested In *">
          <select required value={form.treatment} onChange={onChange('treatment')} className={inputCls} data-testid={`${testIdPrefix}-input-treatment`}>
            <option value="">Select a treatment</option>
            {TREATMENTS.map((t) => <option key={t.slug} value={t.name}>{t.name}</option>)}
            <option value="Other / Not listed">Other / Not listed</option>
          </select>
        </Field>

        <Field label="Preferred City in Vietnam">
          <select value={form.city} onChange={onChange('city')} className={inputCls} data-testid={`${testIdPrefix}-input-city`}>
            <option value="">Select a city</option>
            {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            <optgroup label="Enquiry-based locations">
              {ENQUIRY_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </optgroup>
          </select>
        </Field>

        <Field label="Preferred Date">
          <input
            type="date"
            value={form.date}
            onChange={onChange('date')}
            className={inputCls}
            data-testid={`${testIdPrefix}-input-date`}
          />
        </Field>

        <Field label="Budget Range">
          <select value={form.budget} onChange={onChange('budget')} className={inputCls} data-testid={`${testIdPrefix}-input-budget`}>
            <option value="">Select a range</option>
            {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>

        <Field label="Message / Notes" className="sm:col-span-2">
          <textarea
            value={form.notes}
            onChange={onChange('notes')}
            placeholder="Anything we should know — medical history, dates of travel, specific concerns..."
            rows={4}
            className={`${inputCls} resize-none`}
            data-testid={`${testIdPrefix}-input-notes`}
          />
        </Field>

        <Field label="Upload Photo (optional, if relevant)" className="sm:col-span-2">
          <input
            type="file"
            accept="image/*"
            onChange={onFile}
            className="block w-full text-sm text-slate-500 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-bold file:bg-[#1FA7A1]/10 file:text-[#1FA7A1] hover:file:bg-[#1FA7A1]/20"
            data-testid={`${testIdPrefix}-input-photo`}
          />
          {form.photo && (
            <p className="text-xs text-slate-500 mt-1.5">
              Selected: {form.photo.name} ({Math.round(form.photo.size / 1024)} KB)
            </p>
          )}
        </Field>

        {/* Consent checkbox */}
        <label className="sm:col-span-2 flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={onCheck('consent')}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1FA7A1] focus:ring-[#1FA7A1]/30"
            data-testid={`${testIdPrefix}-input-consent`}
          />
          <span className="text-xs text-slate-600 leading-relaxed">
            I understand <strong className="text-slate-900">VietClinics is a healthcare coordination platform and not a medical provider</strong>. Final diagnosis, medical advice and pricing are provided directly by licensed clinics or doctors.
          </span>
        </label>

        {status.error && (
          <p className="sm:col-span-2 text-sm text-red-500 font-medium">{status.error}</p>
        )}

        <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-2">
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="h-4 w-4 text-[#1FA7A1] flex-shrink-0" />
            Our team or clinic partner will contact you with the next steps.
          </p>
          <button
            type="submit"
            disabled={status.loading}
            className="inline-flex items-center justify-center gap-2 bg-[#1FA7A1] hover:bg-[#178f8a] disabled:opacity-60 text-white font-bold rounded-full px-7 py-3.5 transition-all hover:-translate-y-0.5"
            data-testid={`${testIdPrefix}-form-submit`}
          >
            {status.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Submit Treatment Request
          </button>
        </div>
      </form>
    </div>
  );
};

const inputCls =
  'w-full h-11 px-3.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#1FA7A1] focus:ring-2 focus:ring-[#1FA7A1]/15 transition-colors';

const Field = ({ label, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
    {children}
  </label>
);

export default RequestAppointmentForm;
